import { memo, useMemo, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { isAfter, parseISO, startOfDay } from "date-fns";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";
import { useConstant } from "@hooks/useConstant";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ServiceTemplateScheduleOccurrenceRow } from "../serviceTemplateScheduleOccurrencesViewConfig";
import {
    ServiceTemplateScheduleOccurrenceCallbacks,
    ServiceTemplateScheduleOccurrenceTableOptions,
} from "../serviceTemplateScheduleOccurrenceCallbacks";

export const ServiceTemplateScheduleOccurrenceActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ServiceTemplateScheduleOccurrenceRow,
        unknown
    >) => {
        const { callbacks, options } = useViewTableMeta<
            ServiceTemplateScheduleOccurrenceCallbacks,
            ServiceTemplateScheduleOccurrenceTableOptions
        >(table);
        const [isDeleteConfirmShown, setIsDeleteConfirmShown] = useState(false);
        const occurrenceId = row.original.id;
        const status = row.original.status;
        const closeId = row.original.closeId;
        const applicationDate = row.original.applicationDate;
        const isMutatingOccurrence = options?.isMutatingOccurrence ?? false;
        const isApplied =
            status === ServiceTemplateRecurrenceOccurrenceStatus.APPLIED;
        // Only future application dates offer both "Apply" (now) and "Schedule"
        // (leave it scheduled for its application date); past/today dates only
        // make sense as an immediate "Apply".
        const todayStart = useConstant(() => startOfDay(new Date()));
        const isApplicationDateInFuture = useMemo(
            () =>
                applicationDate
                    ? isAfter(parseISO(applicationDate), todayStart)
                    : false,
            [applicationDate, todayStart],
        );

        if (!callbacks || (isApplied && !closeId)) {
            return <ViewTableCell captureClicks />;
        }

        if (isApplied) {
            return (
                <ViewTableCell captureClicks>
                    <Menu.More>
                        <Menu.Item
                            destructive
                            disabled={isMutatingOccurrence}
                            onClick={() => setIsDeleteConfirmShown(true)}
                        >
                            Delete work
                        </Menu.Item>
                    </Menu.More>
                    {isDeleteConfirmShown && closeId && (
                        <ConfirmationModal
                            show
                            title="Delete this work?"
                            question="This will permanently delete the work and all its tasks. This action cannot be undone."
                            yes="Delete"
                            onConfirm={() => {
                                void callbacks.onDeleteClose(closeId);
                                setIsDeleteConfirmShown(false);
                            }}
                            onReject={() => setIsDeleteConfirmShown(false)}
                            onHide={() => setIsDeleteConfirmShown(false)}
                        />
                    )}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <Menu.More className={viewTableStyles.showOnHover}>
                    {status ===
                    ServiceTemplateRecurrenceOccurrenceStatus.SKIPPED ? (
                        <>
                            <Menu.Item
                                disabled={isMutatingOccurrence}
                                onClick={() =>
                                    void callbacks.onApplySkipped(occurrenceId)
                                }
                            >
                                Apply
                            </Menu.Item>
                            {isApplicationDateInFuture && (
                                <Menu.Item
                                    disabled={isMutatingOccurrence}
                                    onClick={() =>
                                        void callbacks.onUnskip(occurrenceId)
                                    }
                                >
                                    Schedule
                                </Menu.Item>
                            )}
                        </>
                    ) : (
                        <>
                            <Menu.Item
                                disabled={isMutatingOccurrence}
                                onClick={() =>
                                    void callbacks.onApply(occurrenceId)
                                }
                            >
                                Apply
                            </Menu.Item>
                            <Menu.Item
                                destructive
                                disabled={isMutatingOccurrence}
                                onClick={() =>
                                    void callbacks.onSkip(occurrenceId)
                                }
                            >
                                Skip
                            </Menu.Item>
                        </>
                    )}
                </Menu.More>
            </ViewTableCell>
        );
    },
);

ServiceTemplateScheduleOccurrenceActionsCell.displayName =
    "ServiceTemplateScheduleOccurrenceActionsCell";
